import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { CdkDropListGroup, CdkDropList, CdkDrag, CdkDragPlaceholder, DragDropModule } from '@angular/cdk/drag-drop';
import { OverlayModule } from '@angular/cdk/overlay';
import { routing } from './app.routing';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FFlowModule } from '@foblex/flow';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MaterialModule } from './material.module';
import { ToastrModule } from 'ngx-toastr';
import { ColorPickerModule } from 'ngx-color-picker';
import { ColorTwitterModule } from 'ngx-color/twitter';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import { SidebarMenuComponent } from './layout/sidebar-menu/sidebar-menu.component';
import { HomeComponent } from './pages/home/home.component';
import { EditorComponent } from './pages/editor/editor.component';
import { ShipyardComponent } from './pages/home/shipyard/shipyard.component';
import { NewShipyardDialogComponent } from './components/new-shipyard-dialog/new-shipyard-dialog.component';
import { NewVesselDialogComponent } from './components/new-vessel-dialog/new-vessel-dialog.component';
import { VesselPageComponent } from './pages/home/vessel-page/vessel-page.component';
import { StartVersionDialogComponent } from './components/start-version-dialog/start-version-dialog.component';
import { VesselProjectButtonsComponent } from './components/vessel-project-buttons/vessel-project-buttons.component';
import { ICreateLoginComponent, UnauthorizedUserComponent } from '@components/i-create-login/i-create-login.component';
import { ContactFormComponent } from '@components/contact-form/contact-form.component';
import { AuthGuard } from './auth.guard';
import { HttpInterceptorService } from '@service/http-interceptor.service';
import { RequestApprovalDialogComponent } from './components/request-approval-dialog/request-approval-dialog.component';
import { LoadingSpinnerComponent } from '@layout/loading-spinner/loading-spinner.component';
import { RejectPublishDialogComponent } from './components/reject-publish-dialog/reject-publish-dialog.component';
import { ApprovePublishDialogComponent } from './components/approve-publish-dialog/approve-publish-dialog.component';
import { UnlockVesselDialogComponent } from './components/unlock-vessel-dialog/unlock-vessel-dialog.component';
import { RequestAccessDialogComponent } from './components/request-access-dialog/request-access-dialog.component';
import { RequestAccessMessageComponent } from './components/request-access-message/request-access-message.component';
import { RequestApprovalMessageComponent } from './components/request-approval-message/request-approval-message.component';
import { VersionsHistoryComponent } from './pages/versions-history/versions-history.component';
import { VersionsHistorySidebarComponent } from './pages/versions-history/versions-history-sidebar/versions-history-sidebar.component';
import { VersionChangesComponent } from './pages/versions-history/version-changes/version-changes.component';
import { ChannelChangesPanelComponent } from './components/channel-changes-panel/channel-changes-panel.component';
import { VersionApproverPanelComponent } from './components/version-approver-panel/version-approver-panel.component';
import { ChannelGroupChangesPanelComponent } from './components/channel-group-changes-panel/channel-group-changes-panel.component';
import { CategoryChangesPanelComponent } from './components/category-changes-panel/category-changes-panel.component';
import { MimicsChangesPanelComponent } from './components/mimics-changes-panel/mimics-changes-panel.component';
import { OpsChangesPanelComponent } from './components/ops-changes-panel/ops-changes-panel.component';
import { FcuChangesPanelComponent } from './components/fcu-changes-panel/fcu-changes-panel.component';
import { OwnerChangesPanelComponent } from './components/owner-changes-panel/owner-changes-panel.component';
import { VersionHistoryChangesPanelComponent } from './components/version-history-changes-panel/version-history-changes-panel.component';
import { ChannelSideMenuComponent } from '@components/channel/side-menu/side-menu.component';
import { ChannelPanelAlarmComponent } from '@components/channel/channel-panel-alarm/channel-panel-alarm.component';
import { ChannelPanelCategoryComponent } from '@components/channel/channel-panel-category/channel-panel-category.component';
import { ChannelPanelGroupComponent } from '@components/channel/channel-panel-group/channel-panel-group.component';
import { ChannelSelectInputComponent } from '@components/channel/channel-select-input/channel-select-input.component';
import { ChannelSheetComponent } from '@components/channel/channel-sheet/channel-sheet.component';
import { ChannelTextInputComponent } from '@components/channel/channel-text-input/channel-text-input.component';
import { DialogChannelCategoryComponent } from '@components/channel/dialog-channel-category/dialog-channel-category.component';
import { DialogChannelGroupComponent } from '@components/channel/dialog-channel-group/dialog-channel-group.component';
import { MoreOptionsComponent } from '@components/channel/more-options/more-options.component';
import { ChannelPanelAlarmFormComponent } from '@components/channel/channel-alarm-form/channel-alarm-form.component';
import { ChannelPanelAlarmContactComponent } from '@components/channel/channel-panel-alarm-contact/channel-panel-alarm-contact.component';
import { ChannelPanelAlarmFormsListComponent } from '@components/channel/channel-panel-alarm-forms-list/channel-panel-alarm-forms-list.component';
import { ChannelComponent } from './pages/channels/channel.component';

import { Button } from '@components/button/button.component';
import { EditorDropdownComponent } from './components/editor/editor-dropdown/editor-dropdown.component';
import { EditorNavigatorComponent } from './pages/editor/editor-navigator/editor-navigator.component';
import { EditorHeaderComponent } from './pages/editor/editor-header/editor-header.component';
import { EditorSideMenuComponent } from './pages/editor/editor-side-menu/editor-side-menu.component';
import { EditorInputTextComponent } from './components/editor/editor-input-text/editor-input-text.component';
import { EditorInputColorComponent } from './components/editor/editor-input-color/editor-input-color.component';
import { StorkMixedComponent } from './components/editor/stroke-mixed/stroke-mixed.component';
import { NewMimicDialogComponent } from './components/editor/new-mimic-dialog/new-mimic-dialog.component';
import { ToggleSwitchComponent } from '@components/toggle-switch/toggle-switch.component';
import { CanvasService } from '@service/canvas/canvas.service';
import { windowReferenceService } from '@service/canvas/window-reference.service';
import { WindowRef } from '@helpers/windowref';
import { ZoomService } from '@service/canvas/zoom.service';
import { InputWithErrorComponent } from './components/input-with-error/input-with-error.component';
import { InputSelectWithErrorComponent } from './components/input-select-with-error/input-select-with-error.component';
import { TextareaWithErrorComponent } from './components/textarea-with-error/textarea-with-error.component';
import { DesignSideMenuComponent } from './components/editor/design-side-menu/design-side-menu.component';
import { FooterButtonsComponent } from './pages/editor/footer-buttons/footer-buttons.component';
import { TextDesignOptionsComponent } from './components/editor/text-design-options/text-design-options.component';
import { CanvasDesignOptionsComponent } from './components/editor/canvas-design-options/canvas-design-options.component';
import { AlignmentOptionsComponent } from './components/editor/alignment-options/alignment-options.component';
import { PositionOptionsComponent } from './components/editor/position-options/position-options.component';
import { DimensionsOptionsComponent } from './components/editor/dimensions-options/dimensions-options.component';
import { ObjectOrientationComponent } from './components/editor/object-orientation/object-orientation.component';
import { RotationOptionsComponent } from './components/editor/rotation-options/rotation-options.component';
import { CornerRadiusOptionsComponent } from './components/editor/corner-radius-options/corner-radius-options.component';
import { AppearanceOptionsComponent } from './components/editor/appearance-options/appearance-options.component';
import { SettingsSideMenuComponent } from './components/editor/settings-side-menu/settings-side-menu.component';
import { ValveObjectSettingComponent } from './components/editor/settings-side-menu/valve-object-setting/valve-object-setting.component';
import { PumpObjectSettingComponent } from './components/editor/settings-side-menu/pump-object-setting/pump-object-setting.component';
import { MotorObjectSettingComponent } from './components/editor/settings-side-menu/motor-object-setting/motor-object-setting.component';
import { ChannelSearchDialogComponent } from './components/channel-search-dialog/channel-search-dialog.component';
import { ChannelSearchTableComponent } from './components/channel-search-table/channel-search-table.component';
import { ListBubbleSelectorComponent } from './components/list-bubble-selector/list-bubble-selector.component';
import { ButtonElementActionsComponent } from './components/editor/button-element-actions/button-element-actions.component';
import { SystemIntegrationChannelComponent } from './components/editor/system-integration-channel/system-integration-channel.component';
import { AlarmColorIndicatorComponent } from './components/alarm-color-indicator/alarm-color-indicator.component';
import { StatusColorIndicatorComponent } from './components/status-color-indicator/status-color-indicator.component';
import { RangeColorIndicatorComponent } from './components/range-color-indicator/range-color-indicator.component';
import { EditorViewerComponent } from './pages/editor-viewer/editor-viewer.component';
import { PopUpNotificationComponent } from '@components/pop-up-notification/pop-up-notification.component';
import { ConfirmationWindowComponent } from '@components/confirmation-window/confirmation-window.component';
import { MimicViewerModule } from '@mimicViewer/src/lib/mimic-viewer.module';
import { ChannelOverviewPanelComponent } from './components/channel-overview-panel/channel-overview-panel.component';
import { DeleteVersionDialogComponent } from './components/delete-version-dialog/delete-version-dialog.component';
import { LeaveProjectDialogComponent } from './components/leave-project-dialog/leave-project-dialog.component';
import { ZoomRangeComponent } from '@components/editor/zoom-range/zoom-range.component';
import { LayersMenuComponent } from './components/editor/layers-menu/layers-menu.component';
import { VesselInfoContainerComponent } from './pages/home/vessel-page/vessel-info-container/vessel-info-container.component';
import { RemoveAccessDialogComponent } from './components/remove-access-dialog/remove-access-dialog.component';
import { GuestLeftMessageComponent } from './components/guest-left-message/guest-left-message.component';
import { PublishVesselDialogComponent } from './components/publish-vessel-dialog/publish-vessel-dialog.component';
import { SettingsComponent } from './pages/home/settings/settings.component';
import { VesselInformationGridComponent } from './components/vessel-information-grid/vessel-information-grid.component';
import { VesselInformationListComponent } from './components/vessel-information-list/vessel-information-list.component';
import { RecentPagesComponent } from './pages/home/recent-pages/recent-pages.component';
import { HomepageNotificationComponent } from './pages/home/recent-pages/notification/homepage-notification.component';
import { DialogInsertRowsComponent } from '@components/channel/dialog-insert-rows/dialog-insert-rows.component';
import { ShipyardService } from '@service/shipyard.service';
import { MimicActionsService } from '@mimicViewer/src/lib/services/mimic-actions.service';
import { VersionReferencePageComponent } from './components/start-version-dialog/version-reference-page/version-reference-page.component';
import { UpdatesPageComponent } from './components/start-version-dialog/updates-page/updates-page.component';
import { TrialPageComponent } from './components/start-version-dialog/trial-page/trial-page.component';
import { VersionNamePageComponent } from './components/start-version-dialog/version-name-page/version-name-page.component';
import { VersionNameInfoDialogComponent } from './components/start-version-dialog/version-name-info-dialog/version-name-info-dialog.component';
import { RestoreVersionConfirmationDialogComponent } from './components/restore-version-confirmation-dialog/restore-version-confirmation-dialog.component';
import { OpsSettingsComponent } from './pages/ops-settings/ops-settings.component';
import { OpsAccountComponent } from './pages/ops-settings/ops-account/ops-account.component';
import { DutyEngineerComponent } from './pages/ops-settings/duty-engineer/duty-engineer.component';
import { SetInputAddressDialogComponent } from './pages/ops-settings/duty-engineer/set-input-address-dialog/set-input-address-dialog.component';
import { EditCategoriesDialogComponent } from './pages/ops-settings/duty-engineer/edit-categories-dialog/edit-categories-dialog.component';
import { AddDutyEngineerComponent } from '@components/add-duty-engineer/add-duty-engineer.component';
import { SystemColorComponent } from './pages/ops-settings/system-color/system-color.component';
import { EditPermissionDialogComponent } from '@components/edit-permissions-dialog/edit-permissions-dialog.component';
import { NewAccountDialogComponent } from '@components/new-account-dialog/new-account-dialog.component';
import { MessageDialogComponent } from '@components/message-dialog/message-dialog.component';
import { SystemColorContainerComponent } from '@components/system-color/system-color-container/system-color-container.component';
import { SystemColorInputComponent } from '@components/system-color/system-color-input/system-color-input.component';
import { CustomSystemColorContainerComponent } from '@components/system-color/custom-system-color-container/custom-system-color-container.component';
import { NewSystemColorDialogComponent } from '@components/new-system-color-dialog/new-system-color-dialog.component';
import { GraphsComponent } from './pages/ops-settings/graphs/graphs.component';
import { NewGraphDialogComponent } from '@components/graphs/new-graph-dialog/new-graph-dialog.component';
import { ExhaustGraphTableComponent } from '@components/graphs/exhaust-graph-table/exhaust-graph-table.component';
import { BarGraphTableComponent } from '@components/graphs/bar-graph-table/bar-graph-table.component';
import { GaugeGraphTableComponent } from '@components/graphs/gauge-graph-table/gauge-graph-table.component';
import { CylinderGraphTableHeaderComponent } from '@components/graphs/cylinder-graph-table-header/cylinder-graph-table-header.component';
import { CylinderGraphTableRowComponent } from '@components/graphs/cylinder-graph-table-row/cylinder-graph-table-row.component';
import { TurboGraphTableHeaderComponent } from '@components/graphs/turbo-graph-table-header/turbo-graph-table-header.component';
import { TurboGraphTableRowComponent } from '@components/graphs/turbo-graph-table-row/turbo-graph-table-row.component';
import { NewColorStateDialogComponent } from '@components/new-color-state-dialog/new-color-state-dialog.component';
import { PrintDialogComponent } from '@components/print-dialog/print-dialog.component';
import { AutosaveErrorDialogComponent } from '@components/autosave-error-dialog/autosave-error-dialog.component';
import { AlarmGroupSettingComponent } from './pages/channels/alarm-group-setting/alarm-group-setting.component';
import { AlarmGroupDialogComponent } from '@components/alarm-group-setting/alarm-group-dialog/alarm-group-dialog.component';
import { AlarmGroupEditDialogComponent } from '@components/alarm-group-setting/alarm-group-edit-dialog/alarm-group-edit-dialog.component';
import { AlarmRowComponent } from '@components/alarm-group-setting/alarm-group-row/alarm-group-row.component';
import { ReposeSettingComponent } from './pages/channels/repose-setting/repose-setting.component';
import { ReposeDialogComponent } from '@components/repose-setting/repose-dialog/repose-dialog.component';
import { ReposeRowComponent } from '@components/repose-setting/repose-row/repose-row.component';
import { ReposeDetailDialogComponent } from '@components/repose-setting/repose-detail-dialog/repose-detail-dialog.component';
import { NewVesselOptionComponent } from '@components/new-vessel-dialog/new-vessel-option/new-vessel-option.component';
import { NewVesselTemplateOptionComponent } from '@components/new-vessel-dialog/new-vessel-template-option/new-vessel-template-option.component';
import { CreatedFolderPanelComponent } from '@components/created-folder-panel/created-folder-panel.component';
import { DeleteConfirmationDialogComponent } from '@components/delete-confirmation-dialog/delete-confirmation-dialog.component';
import { ExcelColorPickerComponent } from '@components/editor/excel-color-picker/excel-color-picker.component';
import { LockedMimicNotificationComponent } from '@components/editor/locked-mimic-notification/locked-mimic-notification.component';
import { AccessRemovedDialogComponent } from '@components/access-removed-dialog/access-removed-dialog.component';
import { SystemSettingComponent } from './pages/ops-settings/system-setting/system-setting.component';
import { SystemSettingInputComponent } from '@components/system-setting/system-setting-input/system-setting-input.component';
import { SystemSettingOpsComponent } from '@components/system-setting/system-setting-ops/system-setting-ops.component';
import { SystemSettingFcuComponent } from '@components/system-setting/system-setting-fcu/system-setting-fcu.component';
import { SystemSettingEapComponent } from '@components/system-setting/system-setting-eap/system-setting-eap.component';
import { OpsDialogComponent } from '@components/system-setting/ops-dialog/ops-dialog.component';
import { SystemSettingDropdownComponent } from '@components/system-setting/system-setting-dropdown/system-setting-dropdown.component';
import { CommonDialogComponent } from '@components/system-setting/common-dialog/common-dialog.component';
import { FcuSettingComponent } from './pages/ops-settings/system-setting/fcu-setting/fcu-setting.component';
import { ExternalCommunicationDialogComponent } from '@components/system-setting/external-communication-dialog/external-communication-dialog.component';
import { FuSettingInputContainerComponent } from '@components/system-setting/fu-setting-input-container/fu-setting-input-container.component';
import { PortDialogComponent } from '@components/system-setting/port-dialog/port-dialog.component';
import { SlotDialogComponent } from '@components/system-setting/slot-dialog/slot-dialog.component';
import { FcuSettingViewComponent } from './pages/ops-settings/system-setting/fcu-setting-view/fcu-setting-view.component';
import { LibraryDialogComponent } from '@components/editor/library/library-dialog/library-dialog.component';
import { LibraryCategoryDialogComponent } from '@components/editor/library/library-category-dialog/library-category-dialog.component';
import { CreateLibraryComponentContainerComponent } from '@components/channel/create-library-component-container/create-library-component-container.component';
import { LibraryComponentDialogComponent } from '@components/editor/library/library-component-dialog/library-component-dialog.component';
import { DefaultComponentDialogComponent } from '@components/editor/library/default-component-dialog/default-component-dialog.component';
import { OrientationPopupComponent } from '@components/editor/orientation-popup/orientation-popup.component';
import { MimicTabComponent } from './pages/editor/editor-navigator/mimic-tab/mimic-tab.component';
import { LibraryTabComponent } from './pages/editor/editor-navigator/library-tab/library-tab.component';
import { RunningHourSettingComponent } from './pages/channels/running-hour-setting/running-hour-setting.component';
import { EditorMatrixDialogComponent } from '@components/editor-matrix/editor-matrix-dialog.component';
import { ConditionItemComponent } from '@components/editor-matrix/condition-item/condition-item.component';
import { ConditionLogicInputComponent } from '@components/editor-matrix/condition-logic-input/condition-logic-input.component';
import { SelectLibraryComponentDialogComponent } from '@components/editor-matrix/select-library-component-dialog/select-library-component-dialog.component';
import { RunningHourRowComponent } from '@components/running-hour-row/running-hour-row.component';
import { SaveMimicDialogComponent } from '@components/save-mimic-dialog/save-mimic-dialog.component';
import { IntegrationDialogComponent } from '@components/editor/integration-dialog/integration-dialog.component';
import { TriggerConditionDialogComponent } from '@components/editor/trigger-condition-dialog/trigger-condition-dialog.component';
import { TriggerConditionLogicDialogComponent } from '@components/editor/trigger-condition-logic-dialog/trigger-condition-logic-dialog.component';
import { GaugeGraphTableRowComponent } from '@components/graphs/gauge-graph-table-row/gauge-graph-table-row.component';
import { GaugeGraphTableHeaderComponent } from '@components/graphs/gauge-graph-table-header/gauge-graph-table-header.component';
import { ExhaustGasSettingComponent } from './pages/channels/exhaust-gas-setting/exhaust-gas-setting.component';
import { ExhaustSettingComponent } from '@components/exhaust-setting/exhaust-setting.component';
import { ExhaustSettingRowComponent } from '@components/exhaust-setting/exhaust-setting-row/exhaust-setting-row.component';
import { EditExhaustTitleDialogComponent } from '@components/exhaust-setting/edit-exhaust-title-dialog/edit-exhaust-title-dialog.component';
import { BarGraphTableHeaderComponent } from '@components/graphs/bar-graph-table-header/bar-graph-table-header.component';
import { BarGraphTableRowComponent } from '@components/graphs/bar-graph-table-row/bar-graph-table-row.component';
import { BarLabelDialogComponent } from '@components/graphs/bar-label-dialog/bar-label-dialog.component';
import { SensorCableTagComponent } from './pages/channels/sensor-cable-tag/sensor-cable-tag.component';
import { SensorCableTagChannelsComponent } from '@components/sensor-cable-tag-channels/sensor-cable-tag-channels.component';
import { MimicOptionDialogComponent } from '@components/editor/mimic-option-dialog/mimic-option-dialog.component';
import { MimicFolderDialogComponent } from '@components/editor/mimic-folder-dialog/mimic-folder-dialog.component';
import { AddSettingDialogComponent } from '@components/exhaust-setting/add-setting-dialog/add-setting-dialog.component';
import { OutputSettingsComponent } from './pages/ops-settings/output-settings/output-settings.component';
import { ConsoleSettingsComponent } from './pages/ops-settings/console-settings/console-settings.component';
import { AlarmGroupsConsoleSettingsComponent } from './pages/ops-settings/console-settings/alarm-groups-console-settings/alarm-groups-console-settings.component';
import { AlarmGroupOuputSettingsComponent } from '@components/alarm-group-ouput-settings/alarm-group-ouput-settings.component';
import { AddressSelectorsComponent } from '@components/address-selectors/address-selectors.component';
import { OutputSettingComponent } from '@components/alarm-group-ouput-settings/output-setting/output-setting.component';
import { ConfirmationDialogComponent } from '@components/confirmation-dialog/confirmation-dialog.component';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { SlotSelectorComponent } from '@components/slot-selector/slot-selector.component';
import { DigitalOutputSettingsTableComponent } from './pages/ops-settings/output-settings/digital-output-settings-table/digital-output-settings-table.component';
import { AnalogOutputSettingsTableComponent } from './pages/ops-settings/output-settings/analog-output-settings-table/analog-output-settings-table.component';
import { ChannelSearchInputComponent } from '@components/channel-search-input/channel-search-input.component';
import { HighlightPipe } from '@helpers/highlight-text.pipe';
import { DigitalOutputSettingComponent } from '@components/digital-output-setting/digital-output-setting.component';
import { SourceChannelSelectorComponent } from '@components/digital-output-setting/source-channel-selector/source-channel-selector.component';
import { DeviceManagmentComponent } from './pages/device-managment/device-managment.component';
import { ModelDialogComponent } from '@components/model-dialog/model-dialog.component';
import { DeviceDialogComponent } from '@components/device-dialog/device-dialog.component';
import { ReleaseOwnershipDialogComponent } from '@components/release-ownership-dialog/release-ownership-dialog.component';
import { OwnershipTransferMessageComponent } from '@components/ownership-transfer-message/ownership-transfer-message.component';
import { DeviceListComponent } from './pages/device-list/device-list.component';
import { DropdownAccordionComponent } from '@components/dropdown-accordion/dropdown-accordion.component';
import { MultipleDeviceListComponent } from '@components/multiple-device-list/multiple-device-list.component';
import { DeviceModelDropdownComponent } from '@components/device-model-dropdown/device-model-dropdown.component';
import { SingleDeviceListComponent } from '@components/single-device-list/single-device-list.component';
import { NestedDeviceListComponent } from '@components/nested-device-list/nested-device-list.component';
import { LockedVesselNotificationComponent } from '@components/locked-vessel-notification/locked-vessel-notification.component';
import { ChannelsLogicComponent } from './pages/channels/channels-logic/channels-logic.component';
import { FlowStore } from './store/flow-store';
import { FlowActionPanelComponent } from '@components/flow-action-panel/flow-action-panel.component';
import { FlowPaletteComponent } from '@components/flow-palette/flow-palette.component';
import { FlowNodeComponent } from '@components/flow-node/flow-node.component';
import { FlowNodeBodyOutputsComponent } from '@components/flow-node/flow-node-body-outputs/flow-node-body-outputs.component';
import { FlowNodeFooterOutputsComponent } from '@components/flow-node/flow-node-footer-outputs/flow-node-footer-outputs.component';
import { FlowNodeHeaderComponent } from '@components/flow-node/flow-node-header/flow-node-header.component';
import { FlowNodeVoicemailFormComponent } from '@components/flow-node/flow-node-voicemail-form/flow-node-voicemail-form.component';
import { FlowNodeInputsComponent } from '@components/flow-node/flow-node-inputs/flow-node-inputs.component';
import { DescriptionCellComponent } from '@components/description-cell/description-cell.component';
import { EditSessionTimeoutDialog } from '@components/edit-session-timeout-dialog/edit-session-timeout-dialog.component';
import { VesselMimicsPreviewComponent } from './pages/home/vessel-page/vessel-mimics-preview/vessel-mimics-preview.component';
import { DefaultElementSettingsComponent } from '@components/editor/settings-side-menu/default-element-settings/default-element-settings.component';
import { AlarmIndicatorsComponent } from '@components/editor/alarm-indicators/alarm-indicators.component';
import { RangeIndicatorsComponent } from '@components/editor/range-indicators/range-indicators.component';
import { DuplicateMimicDialogComponent } from './components/editor/duplicate-mimic-dialog/duplicate-mimic-dialog.component';
import { DropdownComponent } from './components/dropdown/dropdown.component';
import { SystemSpecificationComponent } from './pages/system-specification/system-specification.component';
import { HardwareSpecificationsComponent } from './pages/system-specification/hardware-specifications/hardware-specifications.component';
import { ToTopComponent } from '@components/to-top/to-top.component';
import { AnchorMenuComponent } from '@components/anchor-menu/anchor-menu.component';
import { SoftwareSpecificationsComponent } from './pages/system-specification/software-specifications/software-specifications.component';
import { OtherSpecificationsComponent } from './pages/system-specification/other-specifications/other-specifications.component';
import { DownloadVersionDialogComponent } from '@components/download-version-dialog/download-version-dialog.component';
import { SimpleDropdownComponent } from '@components/simple-dropdown/simple-dropdown.component';
import { SimpleInputComponent } from '@components/simple-input/simple-input.component';
import { ElectronLoginComponent } from '@components/electron-login/electron-login.component';
import { FcuNumSelectorComponent } from './pages/channels/sensor-cable-tag/fcu-num-selector/fcu-num-selector.component';
import { TerminalButtonComponent } from '@components/system-setting/slot-dialog/terminal-button/terminal-button.component';
import { OpsSettingsConfirmDialogComponent } from '@src/app/pages/ops-settings/ops-settings-confirmation-dialog/ops-settings-confirm-dialog.component';
import { EditGraphDialogComponent } from '@components/graphs/edit-graph-dialog/edit-graph-dialog.component';
import { ControlElementSettingsComponent } from '@components/editor/settings-side-menu/control-element-settings/control-element-settings.component';
import { ToggleInteractionsComponent } from '@components/editor/toggle-interactions/toggle-interactions.component';
import { ButtonElementInteractionsComponent } from '@components/editor/button-element-interactions/button-element-interactions.component';
import { ButtonFlashingStateComponent } from '@components/editor/button-element-interactions/button-flashing-state/button-flashing-state.component';
import { ButtonDisabledStateComponent } from '@components/editor/button-element-interactions/button-disabled-state/button-disabled-state.component';
import { BarElementSettingsComponent } from '@components/editor/bar-element-settings/bar-element-settings.component';
import { GaugeElementSettingsComponent } from '@components/editor/gauge-element-settings/gauge-element-settings.component';
import { GaugeDivisionsDialogComponent } from '@components/editor/gauge-divisions-dialog/gauge-divisions-dialog.component';
import { EapLedSettingsComponent } from './pages/ops-settings/eap-settings/eap-led-settings.component';
import { PatternSelectionDialog } from './pages/ops-settings/eap-settings/pattern-selection-dialog/pattern-selection-dialog.component';
import { LEDPatternSettingDialog } from './pages/ops-settings/eap-settings/led-pattern-setting-dialog/led-pattern-setting-dialog.component';
import { NewPatternSettingDialog } from './pages/ops-settings/eap-settings/new-pattern-setting-dialog/new-pattern-setting-dialog.component';
import { MoreOptionsDropdownComponent } from '@components/more-options/more-options-dropdown.component';
import { SimpleOverlayComponent } from '@components/simple-overlay/simple-overlay.component';
import { ExternalCommSettingsComponent } from './pages/ops-settings/external-comm-settings/external-comm-settings.component';
import { CommunicationProtocolsComponent } from './pages/communication-protocols/communication-protocols.component';
import { CommunicationProtocolDialogComponent } from './pages/communication-protocols/communication-protocol-dialog/communication-protocol-dialog.component';
import { PortsTableComponent } from './pages/ops-settings/external-comm-settings/ports-table/ports-table.component';
import { CeuUnitDialogComponent } from '@components/ceu-unit-dialog/ceu-unit-dialog.component';
import { DuplicateDestinationDialogComponent } from '@components/duplicate-destination-dialog/duplicate-destination-dialog.component';
import { PortSettingsDialogComponent } from '@components/port-settings-dialog/port-settings-dialog.component';
import { ModbusSettingsDialogComponent } from '@components/modbus-settings-dialog/modbus-settings-dialog.component';
import { VesselMenuPrintDialogComponent } from '@components/vessel-menu-print-dialog/vessel-menu-print-dialog.component';
import { LoadingDialogComponent } from '@layout/loading-dialog/loading-dialog.component';

function createTranslateLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
    declarations: [
        AppComponent,
        LoadingSpinnerComponent,
        SidebarMenuComponent,
        HomeComponent,
        EditorComponent,
        RecentPagesComponent,
        ShipyardComponent,
        NewShipyardDialogComponent,
        NewVesselDialogComponent,
        VesselPageComponent,
        StartVersionDialogComponent,
        VesselProjectButtonsComponent,
        NewMimicDialogComponent,
        DuplicateMimicDialogComponent,
        DropdownComponent,
        TerminalButtonComponent,
        ICreateLoginComponent,
        ContactFormComponent,
        UnauthorizedUserComponent,
        RequestApprovalDialogComponent,
        RejectPublishDialogComponent,
        ApprovePublishDialogComponent,
        UnlockVesselDialogComponent,
        RequestAccessDialogComponent,
        RequestAccessMessageComponent,
        RequestApprovalMessageComponent,
        VersionsHistoryComponent,
        VersionsHistorySidebarComponent,
        VersionChangesComponent,
        ChannelChangesPanelComponent,
        VersionApproverPanelComponent,
        ChannelGroupChangesPanelComponent,
        CategoryChangesPanelComponent,
        MimicsChangesPanelComponent,
        OpsChangesPanelComponent,
        FcuChangesPanelComponent,
        OwnerChangesPanelComponent,
        VersionHistoryChangesPanelComponent,
        ChannelComponent,
        ChannelSideMenuComponent,
        MoreOptionsComponent,
        DialogChannelGroupComponent,
        DialogChannelCategoryComponent,
        ChannelTextInputComponent,
        ChannelSheetComponent,
        ChannelSelectInputComponent,
        ChannelPanelGroupComponent,
        ChannelPanelCategoryComponent,
        ChannelPanelAlarmComponent,
        ChannelPanelAlarmFormComponent,
        ChannelPanelAlarmContactComponent,
        ChannelPanelAlarmFormsListComponent,
        Button,
        ZoomRangeComponent,
        EditorDropdownComponent,
        EditorNavigatorComponent,
        EditorHeaderComponent,
        EditorSideMenuComponent,
        EditorInputTextComponent,
        EditorInputColorComponent,
        StorkMixedComponent,
        ToggleSwitchComponent,
        InputWithErrorComponent,
        InputSelectWithErrorComponent,
        TextareaWithErrorComponent,
        DesignSideMenuComponent,
        FooterButtonsComponent,
        TextDesignOptionsComponent,
        CanvasDesignOptionsComponent,
        AlignmentOptionsComponent,
        PositionOptionsComponent,
        DimensionsOptionsComponent,
        ObjectOrientationComponent,
        RotationOptionsComponent,
        CornerRadiusOptionsComponent,
        AppearanceOptionsComponent,
        SettingsSideMenuComponent,
        ValveObjectSettingComponent,
        PumpObjectSettingComponent,
        MotorObjectSettingComponent,
        ChannelSearchDialogComponent,
        ChannelSearchTableComponent,
        ListBubbleSelectorComponent,
        ButtonElementActionsComponent,
        SystemIntegrationChannelComponent,
        AlarmColorIndicatorComponent,
        StatusColorIndicatorComponent,
        RangeColorIndicatorComponent,
        EditorViewerComponent,
        PopUpNotificationComponent,
        ConfirmationWindowComponent,
        ChannelOverviewPanelComponent,
        DeleteVersionDialogComponent,
        LeaveProjectDialogComponent,
        LayersMenuComponent,
        VesselInfoContainerComponent,
        RemoveAccessDialogComponent,
        GuestLeftMessageComponent,
        PublishVesselDialogComponent,
        SettingsComponent,
        VesselInformationGridComponent,
        VesselInformationListComponent,
        HomepageNotificationComponent,
        DialogInsertRowsComponent,
        VersionReferencePageComponent,
        UpdatesPageComponent,
        TrialPageComponent,
        VersionNamePageComponent,
        VersionNameInfoDialogComponent,
        RestoreVersionConfirmationDialogComponent,
        OpsSettingsComponent,
        OpsAccountComponent,
        DutyEngineerComponent,
        OpsSettingsConfirmDialogComponent,
        SetInputAddressDialogComponent,
        EditCategoriesDialogComponent,
        AddDutyEngineerComponent,
        SystemColorComponent,
        EditPermissionDialogComponent,
        NewAccountDialogComponent,
        EditSessionTimeoutDialog,
        MessageDialogComponent,
        SystemColorContainerComponent,
        SystemColorInputComponent,
        CustomSystemColorContainerComponent,
        NewSystemColorDialogComponent,
        GraphsComponent,
        NewGraphDialogComponent,
        ExhaustGraphTableComponent,
        BarGraphTableComponent,
        BarGraphTableHeaderComponent,
        BarGraphTableRowComponent,
        BarLabelDialogComponent,
        BarGraphTableHeaderComponent,
        BarGraphTableRowComponent,
        BarLabelDialogComponent,
        GaugeGraphTableComponent,
        CylinderGraphTableHeaderComponent,
        CylinderGraphTableRowComponent,
        TurboGraphTableHeaderComponent,
        TurboGraphTableRowComponent,
        GaugeGraphTableHeaderComponent,
        GaugeGraphTableRowComponent,
        NewColorStateDialogComponent,
        PrintDialogComponent,
        AutosaveErrorDialogComponent,
        AlarmGroupSettingComponent,
        AlarmGroupDialogComponent,
        AlarmGroupEditDialogComponent,
        AlarmRowComponent,
        ReposeSettingComponent,
        ReposeDialogComponent,
        ReposeRowComponent,
        ReposeDetailDialogComponent,
        RunningHourSettingComponent,
        RunningHourRowComponent,
        NewVesselOptionComponent,
        NewVesselTemplateOptionComponent,
        CreatedFolderPanelComponent,
        DeleteConfirmationDialogComponent,
        ExcelColorPickerComponent,
        LockedMimicNotificationComponent,
        LockedVesselNotificationComponent,
        AccessRemovedDialogComponent,
        SystemSettingComponent,
        SystemSettingInputComponent,
        SystemSettingOpsComponent,
        SystemSettingFcuComponent,
        SystemSettingEapComponent,
        OpsDialogComponent,
        SystemSettingDropdownComponent,
        CommonDialogComponent,
        FcuSettingComponent,
        ExternalCommunicationDialogComponent,
        FuSettingInputContainerComponent,
        PortDialogComponent,
        SlotDialogComponent,
        FcuSettingViewComponent,
        LibraryDialogComponent,
        LibraryCategoryDialogComponent,
        CreateLibraryComponentContainerComponent,
        LibraryComponentDialogComponent,
        DefaultComponentDialogComponent,
        OrientationPopupComponent,
        MimicTabComponent,
        LibraryTabComponent,
        EditorMatrixDialogComponent,
        ConditionItemComponent,
        ConditionLogicInputComponent,
        SelectLibraryComponentDialogComponent,
        SaveMimicDialogComponent,
        IntegrationDialogComponent,
        TriggerConditionDialogComponent,
        TriggerConditionLogicDialogComponent,
        ExhaustGasSettingComponent,
        ExhaustSettingComponent,
        ExhaustSettingRowComponent,
        EditExhaustTitleDialogComponent,
        SensorCableTagComponent,
        SensorCableTagChannelsComponent,
        MimicOptionDialogComponent,
        MimicFolderDialogComponent,
        AddSettingDialogComponent,
        OutputSettingsComponent,
        ConsoleSettingsComponent,
        AlarmGroupsConsoleSettingsComponent,
        AlarmGroupOuputSettingsComponent,
        AddressSelectorsComponent,
        OutputSettingComponent,
        ConfirmationDialogComponent,
        SlotSelectorComponent,
        DigitalOutputSettingsTableComponent,
        AnalogOutputSettingsTableComponent,
        ChannelSearchInputComponent,
        HighlightPipe,
        DigitalOutputSettingComponent,
        SourceChannelSelectorComponent,
        DeviceManagmentComponent,
        ModelDialogComponent,
        DeviceDialogComponent,
        SourceChannelSelectorComponent,
        ReleaseOwnershipDialogComponent,
        OwnershipTransferMessageComponent,
        DeviceListComponent,
        DropdownAccordionComponent,
        MultipleDeviceListComponent,
        DeviceModelDropdownComponent,
        SingleDeviceListComponent,
        NestedDeviceListComponent,
        ChannelsLogicComponent,
        FlowActionPanelComponent,
        FlowPaletteComponent,
        FlowNodeComponent,
        FlowNodeBodyOutputsComponent,
        FlowNodeFooterOutputsComponent,
        FlowNodeHeaderComponent,
        FlowNodeVoicemailFormComponent,
        FlowNodeInputsComponent,
        DescriptionCellComponent,
        VesselMimicsPreviewComponent,
        DefaultElementSettingsComponent,
        AlarmIndicatorsComponent,
        RangeIndicatorsComponent,
        SystemSpecificationComponent,
        HardwareSpecificationsComponent,
        SoftwareSpecificationsComponent,
        OtherSpecificationsComponent,
        ToTopComponent,
        AnchorMenuComponent,
        DownloadVersionDialogComponent,
        SimpleDropdownComponent,
        SimpleInputComponent,
        ElectronLoginComponent,
        FcuNumSelectorComponent,
        EditGraphDialogComponent,
        ControlElementSettingsComponent,
        ToggleInteractionsComponent,
        ButtonElementInteractionsComponent,
        ButtonFlashingStateComponent,
        ButtonDisabledStateComponent,
        BarElementSettingsComponent,
        GaugeElementSettingsComponent,
        GaugeDivisionsDialogComponent,
        EapLedSettingsComponent,
        PatternSelectionDialog,
        LEDPatternSettingDialog,
        NewPatternSettingDialog,
        MoreOptionsDropdownComponent,
        SimpleOverlayComponent,
        CommunicationProtocolsComponent,
        CommunicationProtocolDialogComponent,
        ExternalCommSettingsComponent,
        PortsTableComponent,
        CeuUnitDialogComponent,
        DuplicateDestinationDialogComponent,
        PortSettingsDialogComponent,
        ModbusSettingsDialogComponent,
        VesselMenuPrintDialogComponent,
        LoadingDialogComponent,
    ],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        AppRoutingModule,
        CdkDropList,
        CdkDrag,
        CdkDropListGroup,
        CdkDragPlaceholder,
        BrowserModule,
        BrowserAnimationsModule,
        routing,
        MaterialModule,
        FormsModule,
        ReactiveFormsModule,
        FFlowModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader,
                deps: [HttpClient],
            },
            defaultLanguage: 'en',
        }),
        ToastrModule.forRoot({
            timeOut: 3000,
            positionClass: 'toast-bottom-right',
            preventDuplicates: false,
        }),
        ColorPickerModule,
        ColorTwitterModule,
        MimicViewerModule,
        DragDropModule,
        OverlayModule,
    ],
    providers: [
        AuthGuard,
        FlowStore,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpInterceptorService,
            multi: true,
        },
        CanvasService,
        WindowRef,
        windowReferenceService,
        ZoomService,
        MimicActionsService,
        {
            provide: APP_INITIALIZER,
            useFactory: ApplicationInitializerFactory,
            deps: [ShipyardService, AuthGuard],
            multi: true,
        },
        {
            provide: MAT_DIALOG_DEFAULT_OPTIONS,
            useValue: { autoFocus: false, disableClose: true },
        },
        provideHttpClient(withInterceptorsFromDi()),
    ],
})
export class AppModule {}

export function ApplicationInitializerFactory(shipyardService: ShipyardService, authGuard: AuthGuard) {
    return async () => {
        const isAuthenticated = await authGuard.canActivate();
        if (isAuthenticated) await shipyardService.getList();
    };
}
